/**
 * Cookie consent + Do Not Track (DNT) utilities.
 *
 * Single source of truth for whether tracking (Sentry, Google Analytics)
 * may run. Tracking is only allowed after EXPLICIT consent AND when the
 * user has not sent a Do Not Track signal — DNT always wins, per our
 * Cookie Policy section 8 ("If you have DNT enabled, we will not store
 * analytics or marketing cookies").
 *
 * Storage layout (localStorage key "cookie-consent"):
 *   - "accepted"  → user accepted all cookies
 *   - "declined"  → user rejected non-essential cookies
 *   - "seen"      → banner dismissed without an explicit choice (NOT consent)
 *   - <absent>    → fresh visitor, banner should be shown
 */

const CONSENT_KEY = 'cookie-consent';
const ACCEPTED = 'accepted';
const DECLINED = 'declined';

const listeners = new Set();

/**
 * Read the browser's Do Not Track signal across vendor prefixes.
 * Returns true only when the user has explicitly requested not to be tracked.
 */
export function isDoNotTrackEnabled() {
  try {
    const dnt =
      navigator.doNotTrack ||
      window.doNotTrack ||
      navigator.msDoNotTrack;
    return dnt === '1' || dnt === 'yes';
  } catch {
    return false;
  }
}

/**
 * Raw stored consent value: "accepted" | "declined" | "seen" | null.
 */
export function getStoredConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    return null;
  }
}

/**
 * Has the user made an explicit Accept/Reject choice?
 * ("seen" — dismissed without choosing — is NOT a decision.)
 */
export function hasConsentDecision() {
  const value = getStoredConsent();
  return value === ACCEPTED || value === DECLINED;
}

/**
 * May we run analytics/tracking?
 * Requires explicit acceptance. A DNT signal always overrides and blocks tracking.
 */
export function hasAnalyticsConsent() {
  if (isDoNotTrackEnabled()) return false;
  return getStoredConsent() === ACCEPTED;
}

function persist(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    /* storage unavailable (private mode) — consent kept in-memory only */
  }
}

/**
 * Record explicit acceptance and notify subscribers so tracking can be
 * initialized retroactively in the current session.
 */
export function setConsentAccepted() {
  persist(ACCEPTED);
  notify(true);
}

/**
 * Record explicit rejection and notify subscribers so any deferred
 * tracking init is skipped.
 */
export function setConsentDeclined() {
  persist(DECLINED);
  notify(false);
}

/**
 * Subscribe to consent changes. Callback receives `granted` (boolean).
 * Returns an unsubscribe function.
 */
export function onConsentChange(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function notify(granted) {
  listeners.forEach((cb) => {
    try {
      cb(granted);
    } catch {
      /* a failing listener must not break the consent flow */
    }
  });
}
